const VIEWPORT_GUTTER = 8;
const TOOLTIP_GAP = 6;

export class FloatingTooltipManager {
    private activeTrigger: HTMLElement | null = null;
    private activeTooltip: HTMLElement | null = null;
    private animationFrame: number | null = null;

    constructor(
        private readonly root: HTMLElement,
        private readonly triggerSelector: string,
        private readonly tooltipSelector: string,
    ) {
        this.handlePointerOver = this.handlePointerOver.bind(this);
        this.handlePointerOut = this.handlePointerOut.bind(this);
        this.handleFocusIn = this.handleFocusIn.bind(this);
        this.handleFocusOut = this.handleFocusOut.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.position = this.position.bind(this);
    }

    connect(): void {
        this.configureTriggers();
        this.root.addEventListener("pointerover", this.handlePointerOver);
        this.root.addEventListener("pointerout", this.handlePointerOut);
        this.root.addEventListener("focusin", this.handleFocusIn);
        this.root.addEventListener("focusout", this.handleFocusOut);
        this.root.addEventListener("keydown", this.handleKeyDown);
        const hoveredTrigger = this.root.querySelector<HTMLElement>(`${this.triggerSelector}:hover`);
        if (hoveredTrigger) {
            this.show(hoveredTrigger);
        }
    }

    disconnect(): void {
        this.root.removeEventListener("pointerover", this.handlePointerOver);
        this.root.removeEventListener("pointerout", this.handlePointerOut);
        this.root.removeEventListener("focusin", this.handleFocusIn);
        this.root.removeEventListener("focusout", this.handleFocusOut);
        this.root.removeEventListener("keydown", this.handleKeyDown);
        this.hide();
    }

    private configureTriggers(): void {
        this.root.querySelectorAll<HTMLElement>(this.triggerSelector).forEach((trigger) => {
            trigger.removeAttribute("title");
            trigger.querySelector<HTMLElement>(this.tooltipSelector)?.setAttribute("popover", "manual");
        });
    }

    private handlePointerOver(event: PointerEvent): void {
        const trigger = this.triggerFromEvent(event);
        if (!trigger || trigger === this.activeTrigger) {
            return;
        }

        this.show(trigger);
    }

    private show(trigger: HTMLElement): void {
        const tooltip = trigger.querySelector<HTMLElement>(this.tooltipSelector);
        if (!tooltip) {
            return;
        }

        trigger.removeAttribute("title");
        tooltip.setAttribute("popover", "manual");
        tooltip.style.setProperty("translate", "none");
        this.hide();
        this.activeTrigger = trigger;
        this.activeTooltip = tooltip;
        tooltip.showPopover();
        this.position();
        this.startTracking();
        this.schedulePosition();
    }

    private handlePointerOut(event: PointerEvent): void {
        const trigger = this.triggerFromEvent(event);
        if (!trigger || trigger !== this.activeTrigger) {
            return;
        }

        if (event.relatedTarget instanceof Node && trigger.contains(event.relatedTarget)) {
            return;
        }

        this.hide();
    }

    private handleFocusIn(event: FocusEvent): void {
        const trigger = this.triggerFromEvent(event);
        if (!trigger || trigger === this.activeTrigger) {
            return;
        }

        this.show(trigger);
    }

    private handleFocusOut(event: FocusEvent): void {
        const trigger = this.triggerFromEvent(event);
        if (!trigger || trigger !== this.activeTrigger) {
            return;
        }

        if (event.relatedTarget instanceof Node && trigger.contains(event.relatedTarget)) {
            return;
        }

        this.hide();
    }

    private handleKeyDown(event: KeyboardEvent): void {
        if (event.key === "Escape" && this.activeTooltip) {
            this.hide();
        }
    }

    private triggerFromEvent(event: Event): HTMLElement | null {
        if (!(event.target instanceof Element)) {
            return null;
        }

        const trigger = event.target.closest<HTMLElement>(this.triggerSelector);
        return trigger && this.root.contains(trigger) ? trigger : null;
    }

    private hide(): void {
        this.stopTracking();
        if (this.activeTooltip?.matches(":popover-open")) {
            this.activeTooltip.hidePopover();
        }
        this.activeTrigger = null;
        this.activeTooltip = null;
    }

    private startTracking(): void {
        window.addEventListener("resize", this.position);
        window.addEventListener("scroll", this.position, true);
    }

    private stopTracking(): void {
        window.removeEventListener("resize", this.position);
        window.removeEventListener("scroll", this.position, true);
        if (this.animationFrame !== null) {
            window.cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
    }

    private schedulePosition(): void {
        if (this.animationFrame !== null) {
            window.cancelAnimationFrame(this.animationFrame);
        }
        this.animationFrame = window.requestAnimationFrame(() => {
            this.animationFrame = null;
            this.position();
        });
    }

    private position(): void {
        if (!this.activeTrigger || !this.activeTooltip?.matches(":popover-open")) {
            return;
        }

        const triggerRect = this.activeTrigger.getBoundingClientRect();
        const tooltipRect = this.activeTooltip.getBoundingClientRect();
        const centeredLeft = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        const maximumLeft = window.innerWidth - tooltipRect.width - VIEWPORT_GUTTER;
        const left = Math.max(VIEWPORT_GUTTER, Math.min(centeredLeft, maximumLeft));
        const belowTop = triggerRect.bottom + TOOLTIP_GAP;
        const aboveTop = triggerRect.top - tooltipRect.height - TOOLTIP_GAP;
        const top =
            belowTop + tooltipRect.height <= window.innerHeight - VIEWPORT_GUTTER
                ? belowTop
                : Math.max(VIEWPORT_GUTTER, aboveTop);

        this.activeTooltip.style.left = `${left}px`;
        this.activeTooltip.style.top = `${top}px`;
    }
}
